<script setup lang="ts">
    import { computed, onMounted, watch, ref, type Ref } from 'vue'
    import { useRoute, onBeforeRouteLeave } from 'vue-router'

    import { SavingState } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import EditableValueSeriesSheet from '@common/Components/EditableValueSeriesSheet.vue'
    import SaveButton from '@common/Components/SaveButton.vue'

    import type { KendoSpreadsheetColumn, KendoSpreadsheetValueSerie } from "@Kendo/Components/Spreadsheet/types"
    import type { ResourceCapacityPageReadModel } from '@Quest/Resources/readmodels'

    import { useResourceCapacityPageQueryV1 } from '@Quest/Resources/queries-vue-composables'
    import { useUpdateResourceCapacitiesCommandV1 } from '@Quest/Resources/commands-vue-composables'


    // ----------------------------------------------------
    // Route params
    // ----------------------------------------------------

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const resourceId = computed(() => String(route.params.resourceId ?? ''))


    // ----------------------------------------------------
    // Query
    // ----------------------------------------------------

    const {
        runResourceCapacityPageQueryV1,
        modelResourceCapacityPageQueryV1
    } = useResourceCapacityPageQueryV1(workspaceId, strategyId, resourceId)

    const rm = modelResourceCapacityPageQueryV1 as Ref<ResourceCapacityPageReadModel | null>


    // ----------------------------------------------------
    // Command
    // ----------------------------------------------------

    const {
        invokeUpdateResourceCapacitiesCommandV1,
        succeedUpdateResourceCapacitiesCommandV1
    } = useUpdateResourceCapacitiesCommandV1(workspaceId, strategyId)


    // ----------------------------------------------------
    // Local state
    // ----------------------------------------------------

    const isEditing = ref(false)
    const savingState = ref<SavingState>(SavingState.Saved)


    // ----------------------------------------------------
    // Computed
    // ----------------------------------------------------

    const title = computed(() => `${rm.value?.ResourceName ?? ''}`)

    const stopEditingLabel = computed(() =>
        savingState.value === SavingState.Pending
            ? "Cancel editing"
            : "Back"
    )

    const inputSeries = computed<KendoSpreadsheetValueSerie[]>(() =>
        rm.value
            ? rm.value.Years.map(y => ({
                Id: y.Year.toString(),
                Name: y.Year.toString(),
                Values: y.Values
            }))
            : []
    )

    const columns = computed<KendoSpreadsheetColumn[]>(() => [
        { Label: "Jan" }, { Label: "Feb" }, { Label: "Mar" }, { Label: "Apr" },
        { Label: "May" }, { Label: "Jun" }, { Label: "Jul" }, { Label: "Aug" },
        { Label: "Sep" }, { Label: "Oct" }, { Label: "Nov" }, { Label: "Dec" }
    ])


    // ----------------------------------------------------
    // Methods
    // ----------------------------------------------------

    const startEditing = () =>
    {
        isEditing.value = true
    }

    const cancelEditing = () =>
    {
        if (savingState.value === SavingState.Saved)
        {
            isEditing.value = false
        }
        else if (
            savingState.value === SavingState.Pending &&
            confirm("You have unsaved changes. Are you sure you want to cancel editing?")
        )
        {
            savingState.value = SavingState.Saved
            isEditing.value = false
        }
    }

    const onDataChange = () =>
    {
        savingState.value = SavingState.Pending
    }

    const sheet = ref<any>(null)

    const handleSaveCommand = () =>
    {
        const entries = sheet.value?.getEntriesForSave()

        invokeUpdateResourceCapacitiesCommandV1({
            resourceId: resourceId.value,
            entries
        })

        savingState.value = SavingState.InProgress
    }


    // ----------------------------------------------------
    // Lifecycle
    // ----------------------------------------------------

    onMounted(() =>
    {
        document.title = "Resource | TransparentChoice"
        runResourceCapacityPageQueryV1()
    })


    // ----------------------------------------------------
    // Command succeed watcher
    // ----------------------------------------------------

    watch(
        succeedUpdateResourceCapacitiesCommandV1,
        (current, previous) =>
        {
            if (current && !previous)
            {
                runResourceCapacityPageQueryV1()

                if (savingState.value === SavingState.InProgress)
                {
                    savingState.value = SavingState.Saved
                }
            }
        }
    )


    // ----------------------------------------------------
    // Route guard
    // ----------------------------------------------------

    onBeforeRouteLeave((to, from, next) =>
    {
        if (savingState.value === SavingState.Pending)
        {
            if (confirm("You have unsaved changes. Do you want to save them before leaving?"))
            {
                handleSaveCommand()
                next()
            }
            else
            {
                next()
            }
        }
        else
        {
            next()
        }
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="resources"></CorporateNavigation>


        <div class="container-fluid" v-if="rm" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6>
                        <a class="text-dark" v-sanitizedHref="`/#/enterprise/resources/${workspaceId}/${strategyId}`">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back to resources
                        </a>
                    </h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{rm.ResourceName}}</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <SaveButton v-if="isEditing" :savingState="savingState" @saveChanges="handleSaveCommand()"></SaveButton>
                    <button v-if="isEditing" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="cancelEditing">{{stopEditingLabel}}</button>

                    <button class="btn btn-success" style="padding: 0.7em 2em;" v-if="!isEditing" @click="startEditing">Edit capacity</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div v-if="isEditing">

                <div class="alert alert-warning" role="alert">
                    Warning: Formula entered here will save as values.
                </div>

                <EditableValueSeriesSheet ref="sheet" :propTitle="title" :propSeries="inputSeries" :propColumns="columns" @dataChange="onDataChange" />
            </div>
            <div v-else>
                <table class="table table-bordered table-hover text-center">
                    <thead class="thead-light">
                        <tr>
                            <th rowspan="2" class="align-middle">Year</th>
                            <th colspan="12" class="align-middle">Months</th>
                            <!--<th rowspan="2" class="align-middle table-secondary">Total</th>-->
                        </tr>
                        <tr>
                            <th class="align-middle">1</th>
                            <th class="align-middle">2</th>
                            <th class="align-middle">3</th>
                            <th class="align-middle">4</th>
                            <th class="align-middle">5</th>
                            <th class="align-middle">6</th>
                            <th class="align-middle">7</th>
                            <th class="align-middle">8</th>
                            <th class="align-middle">9</th>
                            <th class="align-middle">10</th>
                            <th class="align-middle">11</th>
                            <th class="align-middle">12</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="y in rm.Years" :key="y.Year">
                            <td class="align-middle">{{y.Year}}</td>
                            <td class="align-middle" v-for="i in 12">
                                {{y.Values[i-1]}}
                            </td>
                            <!--<td class="align-middle table-secondary"></td>-->
                        </tr>

                    </tbody>


                </table>
            </div>
        </div>
    </div>

</template>


